import { cva, type VariantProps } from 'class-variance-authority';

/**
 * ToggleButton override classes — layered ON TOP of the Button `tertiary`
 * CVA via `className` (twMerge: later same-group classes win). Mirrors the
 * Figma "Toggle Icon button" set (file `YZK9r3wDhvw1pY60KlVEIj`, section
 * `3823:16486`, component set `3832:17856`).
 *
 * Why there's no size axis and no resting palette here: Button `tertiary`
 * IS the resting spec verbatim — glass fill (`bg-secondary-50/70` +
 * `web:backdrop-blur`), 1px `border-secondary-100`, hover/pressed one-shade
 * steps, `ring-secondary-600` focus, no shadow/bevel — and Button's
 * icon-only square sizes + `rounded-btn-{size}-outer-top` radii match the
 * Figma toggle geometry exactly. Only the deltas live here:
 *
 *   selected (Figma Type=Active): violet fills primary-50 → -100 (hover) →
 *     -200 (pressed), icon/text primary-800, focus ring primary-900.
 *   outlined + selected: violet border primary-300 → -400 (hover == pressed).
 *   ghost: transparent border in all non-focus states (focus affordance is
 *     the ring alone) — unlike Button's `ghost`, the fill stays (the toggle
 *     ghost is "outlined minus border", per Figma).
 *
 * Per the user's DS decision the Figma DROP_SHADOW is intentionally NOT
 * reproduced — the glass treatment inherited from `tertiary` replaces it.
 * Dark mode is automatic: every token auto-inverts via CSS vars (see
 * Button.variants.ts dark-mode note); no `dark:` classes needed.
 */
export const toggleButtonVariants = cva('', {
  variants: {
    // outlined + unselected = Button tertiary verbatim → intentionally empty.
    variant: { outlined: '', ghost: '' },
    selected: { false: '', true: '' },
  },
  compoundVariants: [
    // Selected palette — violet fills + icon/text tint (both variants).
    // `text-primary-800` drives the web icon via currentColor inheritance;
    // native gets an explicit hex through Button's `iconColor` (see
    // ToggleButton.icons.ts).
    {
      selected: true,
      class:
        'bg-primary-50 text-primary-800 ' +
        'enabled:hover:bg-primary-100 enabled:active:bg-primary-200 ' +
        // `!` required: twMerge can't dedupe `web:`-prefixed ring colours
        // across CVAs (Button.variants.ts base comment; SrButton precedent).
        'web:focus-visible:!ring-primary-900',
    },
    // Outlined + selected — violet border ramp (hover == pressed per Figma).
    {
      variant: 'outlined',
      selected: true,
      class:
        'border-primary-300 ' +
        'enabled:hover:border-primary-400 enabled:active:border-primary-400',
    },
    // Ghost — borderless at rest/hover/pressed, selected or not.
    {
      variant: 'ghost',
      class:
        'border-transparent ' +
        'enabled:hover:border-transparent enabled:active:border-transparent',
    },
  ],
  defaultVariants: { variant: 'outlined', selected: false },
});

export type ToggleButtonVariantProps = VariantProps<typeof toggleButtonVariants>;
